import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Github, Copy, Check } from 'lucide-react';

const bibtex = `@article{zekri2026generalized,
  title={Generalized Discrete Diffusion from Snapshots},
  author={Zekri, Oussama and Uscidda, Th{\\'e}o and Boull{\\'e}, Nicolas and Korba, Anna},
  journal={arXiv preprint arXiv:2603.21342},
  year={2026}
}`;

const openingPoints = [
  'Discrete diffusion should not be limited to mask or uniform corruption.',
  'GDDS makes arbitrary forward noising processes practical at large vocabulary scale.',
  'GDDS trains from snapshots rather than full noising paths, matching the denoiser to its actual target.',
];

const capabilityPills = [
  'Arbitrary noising',
  'Exact corruption',
  'Snapshot ELBO',
  'Semantic kernels',
];

const resultCards = [
  {
    title: 'Main takeaway',
    body: 'Richer forward processes are not just more expressive on paper; they translate into better language-model behavior.',
  },
  {
    title: 'OpenWebText',
    body: 'On OpenWebText, the semantic Gaussian variant reaches 7.65 perplexity, the strongest result among the GDDS variants shown here.',
  },
  {
    title: 'OOD behavior',
    body: 'The same semantic variants also remain strongest across the out-of-distribution validation sets reported in the paper.',
  },
];

function FigureCard({
  src,
  alt,
  caption,
  className = '',
}: {
  src: string;
  alt: string;
  caption: string;
  className?: string;
}) {
  return (
    <figure className={`rounded-[1.5rem] border border-stone-200/80 bg-white/85 p-4 shadow-[0_18px_50px_rgba(31,41,55,0.08)] ${className}`}>
      <img src={src} alt={alt} className="w-full rounded-[1rem]" />
      {caption ? <figcaption className="mt-3 text-sm leading-6 text-stone-600">{caption}</figcaption> : null}
    </figure>
  );
}

const Gdds: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const copyBibtex = async () => {
    await navigator.clipboard.writeText(bibtex);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="mx-auto max-w-6xl space-y-8"
    >
      <section className="rounded-[2rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(255,251,246,0.98),rgba(243,239,231,0.94))] px-6 py-8 shadow-[0_24px_70px_rgba(31,41,55,0.08)] md:px-10 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div>
            <div className="inline-flex rounded-full border border-stone-200 bg-white/70 px-4 py-1 text-sm font-semibold tracking-[0.16em] text-stone-600">
              Project Page
            </div>
            <h1 className="mt-4 font-display text-5xl font-semibold leading-tight text-stone-800 md:text-7xl">
              Generalized Discrete Diffusion from Snapshots
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-stone-700">
              What if discrete diffusion did not have to be stuck with mask or uniform noise?
              GDDS opens that design space up with exact forward corruption, richer semantic
              noising processes, and a snapshot objective aligned with the denoiser&apos;s real task.
            </p>

            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium text-stone-600">
              <span>Oussama Zekri</span>
              <span>Theo Uscidda</span>
              <span>Nicolas Boulle</span>
              <span>Anna Korba</span>
            </div>

          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <a
                href="https://arxiv.org/abs/2603.21342"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full bg-[#5f6d52] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#49543f]"
              >
                <FileText className="mr-2 h-4 w-4" />
                Paper
              </a>
              <a
                href="https://github.com/ozekri/gdds"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-5 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100"
              >
                <Github className="mr-2 h-4 w-4" />
                Code
              </a>
              <a
                href="https://huggingface.co/papers/2603.21342"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-5 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100"
              >
                <span className="mr-2 text-base leading-none" aria-hidden="true">🤗</span>
                Hugging Face
              </a>
              <a
                href="#bibtex"
                className="inline-flex items-center rounded-full border border-stone-200 bg-white/80 px-5 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100"
              >
                <Copy className="mr-2 h-4 w-4" />
                BibTeX
              </a>
            </div>

            <div className="rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-5 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8a7767]">At a glance</p>
              <div className="mt-4 space-y-3">
                {openingPoints.map((point) => (
                  <div
                    key={point}
                    className="rounded-2xl bg-[linear-gradient(135deg,rgba(250,248,244,0.96),rgba(243,239,231,0.9))] px-4 py-3 text-sm leading-6 text-stone-700"
                  >
                    {point}
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {capabilityPills.map((pill) => (
                  <span
                    key={pill}
                    className="rounded-full border border-stone-200 bg-[#f6f2ec] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-stone-600"
                  >
                    {pill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-4 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <video
            className="w-full rounded-[1.25rem] bg-[#0f1218] shadow-lg"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          >
            <source src="/files/publications/gdds/videos/gdds_forward_noising_teaser.mp4" type="video/mp4" />
            Your browser does not support the teaser video.
          </video>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            The forward process itself can encode structure, instead of relying on blind token corruption.
          </p>
        </div>

        <div className="rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-6 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6f7f5f]">The pitch</p>
          <h2 className="mt-3 font-display text-4xl font-semibold text-stone-800">
            Discrete diffusion gets much more interesting once the forward process becomes a modeling choice.
          </h2>
          <p className="mt-4 text-base leading-8 text-stone-700">
            Existing methods made absorbing or uniform corruption the default because richer
            transitions are hard to handle exactly. GDDS changes that. It gives an exact noising
            algorithm for general time-inhomogeneous processes and trains the model from snapshots
            rather than full sampled paths.
          </p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3 lg:items-stretch">
        <div className="flex h-full flex-col rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-5 shadow-[0_18px_50px_rgba(31,41,55,0.08)]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8a7767]">Why it was hard</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-800">
            Exact transitions become ugly fast.
          </h2>
          <p className="mt-3 text-sm leading-7 text-stone-700">
            Beyond mask or uniform noise, exact forward transitions are governed by a time-ordered
            matrix exponential. That is the main technical bottleneck that kept discrete diffusion
            narrow.
          </p>
          <img
            src="/files/publications/gdds/images/te-exp-ugly.png"
            alt="Time-ordered exponential illustration."
            className="mt-5 w-full rounded-[1rem]"
          />
          <div className="mt-4 rounded-2xl bg-[linear-gradient(135deg,rgba(250,248,244,0.96),rgba(243,239,231,0.9))] px-4 py-4 text-sm leading-6 text-stone-700">
            This intractable transition object is exactly why richer discrete noising stayed mostly
            out of reach before GDDS.
          </div>
          <div className="mt-3 rounded-2xl bg-[linear-gradient(135deg,rgba(250,248,244,0.96),rgba(243,239,231,0.9))] px-4 py-4 text-sm leading-6 text-stone-700">
            Once the forward process depends on time and structure, exact sampling stops being a
            small implementation detail and becomes the central obstacle.
          </div>
        </div>

        <div className="flex h-full flex-col rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-5 shadow-[0_18px_50px_rgba(31,41,55,0.08)]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6f7f5f]">Key ingredient</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-800">
            Exact uniformization-based noising.
          </h2>
          <p className="mt-3 text-sm leading-7 text-stone-700">
            The forward corruption algorithm only needs Poisson sampling and column access to the
            rate matrix. No full matrix exponential is computed, and batching long sequences stays
            easy to parallelize.
          </p>
          <video
            className="mt-5 w-full rounded-[1rem] bg-[#0f1218]"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          >
            <source src="/files/publications/gdds/videos/forward_sampling_equivalence.mp4" type="video/mp4" />
            Your browser does not support the forward sampling video.
          </video>
          <div className="mt-4 grid gap-3">
            <div className="rounded-2xl bg-[#eef2e9] px-4 py-3 text-sm text-[#49543f]">
              The sampler matches the exact forward corruption distribution.
            </div>
            <div className="rounded-2xl bg-[#eef2e9] px-4 py-3 text-sm text-[#49543f]">
              In practice it needs Poisson draws and column access, not a giant matrix exponential.
            </div>
          </div>
        </div>

        <div className="flex h-full flex-col rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-5 shadow-[0_18px_50px_rgba(31,41,55,0.08)]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#7d6a74]">Training view</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-stone-800">
            Train from snapshots, not full paths.
          </h2>
          <p className="mt-3 text-sm leading-7 text-stone-700">
            GDDS learns directly from corrupted snapshots at a given time step. The resulting
            objective is aligned with the denoiser&apos;s real target: recovering the clean token
            posterior from that noisy snapshot.
          </p>
          <img
            src="/files/publications/gdds/images/path-vs-snap.png"
            alt="Comparison between pathwise and snapshot training."
            className="mt-5 w-full rounded-[1rem]"
          />
          <div className="mt-4 rounded-2xl bg-[linear-gradient(135deg,rgba(245,239,232,0.96),rgba(250,248,244,0.92))] px-4 py-4 text-sm leading-6 text-stone-700">
            This change alone improves the generation frontier, even before taking advantage of the
            richer semantic kernels.
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
        <div className="flex h-full flex-col rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-6 shadow-[0_18px_50px_rgba(31,41,55,0.08)]">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6f7f5f]">Semantic noising</p>
          <h2 className="mt-3 font-display text-4xl font-semibold text-stone-800">
            Corruption can respect token similarity.
          </h2>
          <p className="mt-4 text-base leading-8 text-stone-700">
            In language, arbitrary noising means going beyond blind token corruption. GDDS can use
            semantic kernels in embedding space, pushing probability mass toward nearby words
            instead of treating every replacement as equally plausible.
          </p>
          <div className="mt-5 rounded-2xl bg-[linear-gradient(135deg,rgba(238,242,233,0.9),rgba(250,248,244,0.9))] px-5 py-4 text-sm leading-7 text-stone-700">
            In the semantic setting, replacements are guided by neighborhood structure in embedding
            space instead of being sampled as unrelated tokens.
          </div>
        </div>

        <div className="flex h-full flex-col justify-between gap-6">
          <div className="rounded-[1.5rem] border border-stone-200/80 bg-white/85 p-4 shadow-[0_18px_50px_rgba(31,41,55,0.08)]">
            <video
              className="w-full rounded-[1rem] bg-[#0f1218]"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            >
              <source src="/files/publications/gdds/videos/cli_anim.mp4" type="video/mp4" />
              Your browser does not support the CLI animation video.
            </video>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-stone-200/80 bg-[#eef2e9] px-4 py-3 text-sm text-[#49543f]">
              Nearby words receive more corruption mass than unrelated ones.
            </div>
            <div className="rounded-2xl border border-stone-200/80 bg-[#eef2e9] px-4 py-3 text-sm text-[#49543f]">
              This brings some of the structure of continuous diffusion into the discrete setting.
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-6 shadow-[0_20px_60px_rgba(31,41,55,0.08)] md:p-8">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8a7767]">Results</p>
            <h2 className="mt-3 font-display text-4xl font-semibold text-stone-800">
              Better noising also means better behavior.
            </h2>
            <p className="mt-4 text-base leading-8 text-stone-700">
              The empirical picture is fairly clean. GDDS improves both in-distribution quality and
              out-of-distribution robustness, with the semantic variants standing out the most.
            </p>
            <div className="mt-6 grid gap-3">
              {resultCards.slice(0, 2).map((card) => (
                <div
                  key={card.title}
                  className="rounded-2xl bg-[linear-gradient(135deg,rgba(250,248,244,0.96),rgba(243,239,231,0.9))] px-4 py-4"
                >
                  <div className="text-sm font-semibold uppercase tracking-[0.14em] text-stone-500">
                    {card.title}
                  </div>
                  <p className="mt-2 text-sm leading-6 text-stone-700">{card.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid items-stretch gap-6 md:grid-cols-2">
            <div className="flex h-full flex-col justify-between gap-6">
              <FigureCard
                src="/files/publications/gdds/images/zero-shot.png"
                alt="Zero-shot generalization comparison for GDDS."
                caption=""
              />
              <div className="rounded-2xl bg-[linear-gradient(135deg,rgba(250,248,244,0.96),rgba(243,239,231,0.9))] px-4 py-4">
                <div className="text-sm font-semibold uppercase tracking-[0.14em] text-stone-500">
                  {resultCards[2].title}
                </div>
                <p className="mt-2 text-sm leading-6 text-stone-700">{resultCards[2].body}</p>
              </div>
            </div>
            <div className="flex h-full flex-col justify-between gap-6">
              <FigureCard
                src="/files/publications/gdds/images/gen-ppl.png"
                alt="Generation versus perplexity frontier under snapshot training."
                caption=""
              />
              <div className="rounded-2xl bg-[linear-gradient(135deg,rgba(245,239,232,0.96),rgba(250,248,244,0.92))] px-4 py-4">
                <div className="text-sm font-semibold uppercase tracking-[0.14em] text-stone-500">
                  Snapshot objective
                </div>
                <p className="mt-2 text-sm leading-6 text-stone-700">
                  Part of the gain comes from the training view itself: learning from snapshots is
                  already better aligned with the denoiser&apos;s target than pathwise supervision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="bibtex" className="rounded-[1.75rem] border border-stone-200/80 bg-white/85 p-6 shadow-[0_20px_60px_rgba(31,41,55,0.08)] md:p-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-4xl font-semibold text-stone-800">BibTeX</h2>
            <p className="mt-2 text-sm text-stone-600">Citation for the GDDS preprint.</p>
          </div>
          <button
            onClick={copyBibtex}
            className="inline-flex items-center rounded-full bg-[#5f6d52] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#49543f]"
          >
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre className="overflow-x-auto rounded-[1.25rem] bg-[#4f4339] px-5 py-4 text-sm leading-7 text-stone-100">
          <code>{bibtex}</code>
        </pre>
      </section>
    </motion.div>
  );
};

export default Gdds;
